// ce-autoplay-video.js — keeps a muted looping <video> actually looping, and lazy-loads it.
// What the bare `autoplay` attribute cannot do:
//   • load NOTHING until the section approaches the viewport (IntersectionObserver + preload="none"),
//   • play/pause with viewport and tab visibility, so a browser-forced pause never sticks,
//   • retry a play() the environment refused (iOS Low Power Mode, Android Data/Battery Saver,
//     ad in-app webviews) on the next user gesture or scroll.
/* Wrapped so a second include (two video sections on one page) cannot throw "already declared":
   classic scripts execute once per <script> tag, top-level classes are shared lexical bindings. */
(() => {
if (customElements.get('ce-autoplay-video')) return;
class CeAutoplayVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    // properties, not only attributes: some in-app webviews ignore the attributes
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.seen = false;
    this.onVis = () => {
      if (!document.hidden && this.seen) this.tryPlay();
    };
    this.onGesture = () => {
      if (this.seen && this.video.paused) this.tryPlay();
    };
    this.io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this.seen = true;
            this.tryPlay();
          } else if (!this.video.paused) {
            this.video.pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    // observe the video, not the host: the host is display:contents (no box → never intersects)
    this.io.observe(this.video);
    document.addEventListener('visibilitychange', this.onVis);
    for (const t of ['touchend', 'click', 'scroll']) {
      document.addEventListener(t, this.onGesture, { passive: true });
    }
  }
  tryPlay() {
    if (this.video.readyState === 0) this.video.load();
    const p = this.video.play();
    if (p) p.catch(() => {}); // refused now → the gesture/visibility listeners retry later
  }
  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    document.removeEventListener('visibilitychange', this.onVis);
    for (const t of ['touchend', 'click', 'scroll']) {
      document.removeEventListener(t, this.onGesture);
    }
  }
}
customElements.define('ce-autoplay-video', CeAutoplayVideo);
})();
